import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, ButtonBase, Link, Typography } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import type { ProfileMediaItem } from '../../services/mediaContracts';
import { resolveTweetUrl } from '../../services/mediaUtils';
import { renderRichText, richLinkSx, type MentionClickHandler } from './richText';

interface TweetCaptionProps {
  item: ProfileMediaItem;
  compact?: boolean;
  onMentionClick?: MentionClickHandler;
}

const COLLAPSED_CHAR_LIMIT = 180;
const COLLAPSED_LINE_LIMIT = 4;

const formatTweetDate = (createdAt?: string): string | null => {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const day = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });

  return `${time} · ${day}`;
};

const isLongCaption = (text?: string): boolean => {
  if (!text) return false;
  return (
    text.length > COLLAPSED_CHAR_LIMIT ||
    text.split('\n').length > COLLAPSED_LINE_LIMIT
  );
};

const TweetCaption: React.FC<TweetCaptionProps> = ({
  item,
  compact,
  onMentionClick,
}) => {
  const handle = item.author;
  const displayName = item.authorName || (handle ? `@${handle}` : 'Unknown');
  const formattedDate = useMemo(() => formatTweetDate(item.createdAt), [item.createdAt]);
  const longCaption = isLongCaption(item.text);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [item.tweetId, item.downloadUrl]);

  const body = useMemo(
    () => renderRichText(item.text, { facets: item.facets, onMentionClick }),
    [item.text, item.facets, onMentionClick]
  );
  const postUrl = useMemo(
    () =>
      resolveTweetUrl({
        tweetId: item.tweetId,
        author: item.author,
        tweetUrl: item.tweetUrl,
      }),
    [item.tweetId, item.author, item.tweetUrl]
  );

  if (!item.text && !handle) return null;

  const openAuthor = () => {
    if (handle && onMentionClick) onMentionClick(handle);
  };

  return (
    <Box
      sx={{
        px: compact ? 0 : 2,
        py: compact ? 0 : 2,
        borderTop: compact ? 'none' : '1px solid #2F3336',
        backgroundColor: compact ? 'transparent' : '#16181C',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          maxWidth: 720,
          mx: 'auto',
          alignItems: 'flex-start',
        }}
      >
        <Avatar
          src={item.authorAvatar}
          alt={displayName}
          imgProps={{ referrerPolicy: 'no-referrer' }}
          onClick={handle && onMentionClick ? openAuthor : undefined}
          sx={{
            width: compact ? 36 : 40,
            height: compact ? 36 : 40,
            bgcolor: '#2F3336',
            flexShrink: 0,
            cursor: handle && onMentionClick ? 'pointer' : 'default',
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 0.5,
              rowGap: 0.25,
              mb: item.text ? 0.75 : 0,
            }}
          >
            {handle ? (
              onMentionClick ? (
                <Box
                  component="button"
                  type="button"
                  onClick={openAuthor}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    border: 0,
                    background: 'none',
                    p: 0,
                    m: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                    color: 'inherit',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ fontWeight: 700, color: '#E7E9EA', fontSize: '0.9375rem' }}
                  >
                    {displayName}
                  </Typography>
                  {item.authorVerified && (
                    <VerifiedIcon sx={{ fontSize: 16, color: '#1D9BF0' }} />
                  )}
                </Box>
              ) : (
                <Link
                  href={`https://x.com/${handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'inherit',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ fontWeight: 700, color: '#E7E9EA', fontSize: '0.9375rem' }}
                  >
                    {displayName}
                  </Typography>
                  {item.authorVerified && (
                    <VerifiedIcon sx={{ fontSize: 16, color: '#1D9BF0' }} />
                  )}
                </Link>
              )
            ) : (
              <Typography sx={{ fontWeight: 700, color: '#E7E9EA', fontSize: '0.9375rem' }}>
                {displayName}
              </Typography>
            )}

            {handle && (
              onMentionClick ? (
                <Box
                  component="button"
                  type="button"
                  onClick={openAuthor}
                  sx={{
                    ...richLinkSx,
                    color: '#71767B',
                    fontSize: '0.875rem',
                    lineHeight: 1.43,
                  }}
                >
                  @{handle}
                </Box>
              ) : (
                <Typography component="span" variant="body2" sx={{ color: '#71767B' }}>
                  @{handle}
                </Typography>
              )
            )}

            {formattedDate && (
              <>
                <Typography component="span" variant="body2" sx={{ color: '#71767B' }}>
                  ·
                </Typography>
                <Typography component="span" variant="body2" sx={{ color: '#71767B' }}>
                  {formattedDate}
                </Typography>
              </>
            )}
          </Box>

          {item.text && (
            <>
              <Typography
                component="div"
                variant="body1"
                sx={{
                  color: '#E7E9EA',
                  lineHeight: 1.5,
                  fontSize: '0.9375rem',
                  wordBreak: 'break-word',
                  ...(compact && longCaption && !expanded
                    ? {
                        display: '-webkit-box',
                        WebkitLineClamp: COLLAPSED_LINE_LIMIT,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        whiteSpace: 'normal',
                      }
                    : {
                        whiteSpace: 'pre-wrap',
                      }),
                }}
              >
                {body}
              </Typography>

              {compact && longCaption && (
                <ButtonBase
                  onClick={() => setExpanded((v) => !v)}
                  aria-expanded={expanded}
                  sx={{
                    mt: 0.75,
                    color: '#1D9BF0',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    justifyContent: 'flex-start',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {expanded ? 'Show less' : 'Show more'}
                </ButtonBase>
              )}
            </>
          )}

          {postUrl && (
            <Link
              href={postUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                mt: compact ? 1.25 : 1.5,
                color: '#1D9BF0',
                fontSize: '0.8125rem',
                fontWeight: 600,
                textDecoration: 'none',
                minHeight: 32,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />
              View thread & replies on X
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TweetCaption;
